import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, ArrowRight } from "lucide-react";

const TEST_TYPES = [
  { value: "technical", label: "Technical & Skills-Based", desc: "Domain expertise and technical knowledge" },
  { value: "psychometric", label: "Psychometric Assessment", desc: "Personality traits and cognitive abilities" },
  { value: "emotional_intelligence", label: "Emotional Intelligence", desc: "EQ, empathy, and interpersonal skills" },
  { value: "scenario_based", label: "Scenario-Based (Toughness)", desc: "High-pressure decision making" },
];

export default function PipelineInputForm({
  formData,
  focusAreasInput,
  onFieldChange,
  onFocusAreasChange,
  onSubmit,
}) {
  return (
    <Card data-testid="pipeline-input-form">
      <CardHeader>
        <CardTitle>Test Configuration</CardTitle>
        <CardDescription>
          Provide job details and customize test parameters. AI will analyze and generate relevant questions.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div>
          <Label>Test Types * (Select one or more)</Label>
          <p className="text-xs text-gray-500 mb-3">Choose assessment approaches to combine</p>
          <div className="space-y-2 border border-gray-200 rounded-lg p-4 bg-gray-50">
            {TEST_TYPES.map((type) => (
              <label
                key={type.value}
                className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg bg-white hover:bg-gray-50 cursor-pointer transition-colors"
              >
                <input
                  type="checkbox"
                  checked={formData.test_types?.includes(type.value) || false}
                  onChange={(e) => {
                    const currentTypes = formData.test_types || [];
                    const newTypes = e.target.checked
                      ? [...currentTypes, type.value]
                      : currentTypes.filter((t) => t !== type.value);
                    onFieldChange("test_types", newTypes);
                  }}
                  className="mt-1 w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                  data-testid={`test-type-${type.value}`}
                />
                <div className="flex-1">
                  <div className="font-medium text-sm text-gray-900">{type.label}</div>
                  <div className="text-xs text-gray-600">{type.desc}</div>
                </div>
              </label>
            ))}
          </div>
          {formData.test_types && formData.test_types.length === 0 && (
            <p className="text-xs text-red-600 mt-2">Please select at least one test type</p>
          )}
        </div>

        <div>
          <Label htmlFor="role_title">Role Title *</Label>
          <Input
            id="role_title"
            placeholder="e.g., Senior Cybersecurity Analyst"
            value={formData.role_title}
            onChange={(e) => onFieldChange("role_title", e.target.value)}
            data-testid="role-title-input"
          />
        </div>

        <div>
          <Label htmlFor="job_description">Job Description *</Label>
          <Textarea
            id="job_description"
            placeholder="Paste the full job description including responsibilities, requirements, and skills..."
            rows={8}
            value={formData.job_description}
            onChange={(e) => onFieldChange("job_description", e.target.value)}
            data-testid="job-description-input"
          />
        </div>

        <div>
          <Label>Seniority Level</Label>
          <Select value={formData.seniority_level} onValueChange={(val) => onFieldChange("seniority_level", val)}>
            <SelectTrigger data-testid="seniority-level-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="junior">Junior (0-2 years)</SelectItem>
              <SelectItem value="mid">Mid-Level (3-5 years)</SelectItem>
              <SelectItem value="senior">Senior (5-8 years)</SelectItem>
              <SelectItem value="lead">Lead (8-12 years)</SelectItem>
              <SelectItem value="executive">Executive (12+ years)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="focus_areas">Focus Areas (comma-separated)</Label>
          <Input
            id="focus_areas"
            placeholder="e.g., SIEM, Threat Detection, Incident Response"
            value={focusAreasInput}
            onChange={(e) => onFocusAreasChange(e.target.value)}
            data-testid="focus-areas-input"
          />
          <p className="text-xs text-gray-500 mt-1">Separate multiple areas with commas</p>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div>
            <Label htmlFor="question_count">Question Count</Label>
            <Input
              id="question_count"
              type="number"
              min={10}
              max={50}
              value={formData.target_question_count}
              onChange={(e) => onFieldChange("target_question_count", parseInt(e.target.value))}
              data-testid="question-count-input"
            />
          </div>
          <div>
            <Label htmlFor="time_limit">Time Limit (min)</Label>
            <Input
              id="time_limit"
              type="number"
              min={15}
              max={120}
              value={formData.time_limit_minutes}
              onChange={(e) => onFieldChange("time_limit_minutes", parseInt(e.target.value))}
              data-testid="time-limit-input"
            />
          </div>
          <div>
            <Label htmlFor="passing_score">Passing Score (%)</Label>
            <Input
              id="passing_score"
              type="number"
              min={50}
              max={100}
              value={formData.passing_score}
              onChange={(e) => onFieldChange("passing_score", parseInt(e.target.value))}
              data-testid="passing-score-input"
            />
          </div>
        </div>

        <Button
          onClick={onSubmit}
          className="w-full bg-primary hover:bg-primary/90 text-white"
          size="lg"
          data-testid="generate-test-btn"
        >
          <Sparkles className="w-5 h-5 mr-2" />
          Generate Test with AI
          <ArrowRight className="w-5 h-5 ml-2" />
        </Button>
      </CardContent>
    </Card>
  );
}
